import { useEffect } from 'react';

interface OrganizationSchemaProps {
  name?: string;
  description?: string;
  logo?: string;
  url?: string;
  phone?: string;
  email?: string;
  address?: {
    streetAddress?: string;
    addressLocality?: string;
    addressCountry?: string;
    postalCode?: string;
  };
  socialProfiles?: string[];
}

interface ServiceSchemaProps {
  name: string;
  description: string;
  url?: string;
  image?: string;
  areaServed?: string[];
  serviceType?: string;
}

interface BreadcrumbSchemaProps {
  items: Array<{
    name: string;
    url: string;
  }>;
}

// Organization Schema
export function OrganizationSchema({
  name = 'Moreflex Aviation',
  description = 'VIP airport services and travel assistance',
  logo = 'https://www.moreflexaviation.com/Final_logo_6.png',
  url = 'https://www.moreflexaviation.com',
  phone = '+254 (114) 660-056',
  email = 'info@moreflexaviation.com',
  address = {
    streetAddress: '80100',
    addressLocality: 'Mombasa',
    addressCountry: 'KE',
    postalCode: '80100',
  },
  socialProfiles = [],
}: OrganizationSchemaProps) {
  useEffect(() => {
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name,
      description,
      url,
      logo,
      telephone: phone,
      email,
      address: {
        '@type': 'PostalAddress',
        streetAddress: address.streetAddress,
        addressLocality: address.addressLocality,
        addressCountry: address.addressCountry,
        postalCode: address.postalCode,
      },
      sameAs: socialProfiles,
      contactPoint: {
        '@type': 'ContactPoint',
        contactType: 'Customer Support',
        telephone: phone,
        email,
        areaServed: 'Worldwide',
        availableLanguage: 'English',
      },
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, [name, description, logo, url, phone, email, address, socialProfiles]);

  return null;
}

// Service Schema
export function ServiceSchema({
  name,
  description,
  url = 'https://www.moreflexaviation.com/services',
  image,
  areaServed = ['Worldwide'],
  serviceType = 'Aviation Services',
}: ServiceSchemaProps) {
  useEffect(() => {
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name,
      description,
      url,
      image,
      serviceType,
      areaServed: areaServed.map(area => ({
        '@type': 'Place',
        name: area,
      })),
      provider: {
        '@type': 'Organization',
        name: 'Moreflex Aviation',
        url: 'https://www.moreflexaviation.com',
      },
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, [name, description, url, image, areaServed, serviceType]);

  return null;
}

// Breadcrumb Schema
export function BreadcrumbSchema({
  items,
}: BreadcrumbSchemaProps) {
  useEffect(() => {
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: items.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: item.name,
        item: item.url,
      })),
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, [items]);

  return null;
}

// Local Business Schema
export function LocalBusinessSchema({
  name = 'Moreflex Aviation',
  description = 'VIP airport services and travel assistance',
  phone = '+254 (114) 660-056',
  email = 'info@moreflexaviation.com',
  address = {
    streetAddress: '80100',
    addressLocality: 'Mombasa',
    addressCountry: 'KE',
    postalCode: '80100',
  },
}: OrganizationSchemaProps) {
  useEffect(() => {
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      name,
      description,
      telephone: phone,
      email,
      url: 'https://www.moreflexaviation.com',
      address: {
        '@type': 'PostalAddress',
        streetAddress: address.streetAddress,
        addressLocality: address.addressLocality,
        addressCountry: address.addressCountry,
        postalCode: address.postalCode,
      },
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, [name, description, phone, email, address]);

  return null;
}
